/* global gdAlertify */
import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import axiosInstance from "../../config/axiosConfig";
import { formatMoney } from "../../utils/utils";
import { FIRMA_DEFAULT } from "../../utils/firmaCorreo";
import {
    TIPOS_CORREO_COBRANZA, renderizarVariables, textoInicialDeTipo, variablesDesconocidas, variablesEnTexto,
} from "../../utils/variablesCorreoCobranza";
import { mensajeError } from "./enviosUi";
import { imagenComoUrl, mesActual } from "./honorariosWsp";
import MonthInput from "../components/MonthInput";

const labelTipo = (clave) => TIPOS_CORREO_COBRANZA.find((t) => t.clave === clave)?.label || clave;

/**
 * Prueba del correo de cobranza de un paciente: elige período, paciente y tipo de envío (por
 * defecto el que detecta el sistema) y arma el asunto y el texto con sus variables, igual que el
 * envío masivo. El PDF del detalle lo adjunta el backend al enviar. La prueba no marca la notificación.
 *
 * onListo({ asunto, cuerpo, cobranza: { periodo, paciente_id }, paciente, tipo, faltan }) | onListo(null)
 */
const CobranzaPrueba = ({ onListo }) => {
    const [periodo, setPeriodo] = useState(mesActual());
    const [info, setInfo] = useState(null);
    const [cargando, setCargando] = useState(false);
    const [pacienteId, setPacienteId] = useState(null);
    const [tipo, setTipo] = useState("");
    const [templateId, setTemplateId] = useState("");
    const [pdf, setPdf] = useState({ cargando: false, url: null });

    useEffect(() => () => { if (pdf.url) URL.revokeObjectURL(pdf.url); }, [pdf.url]);

    const limpiar = () => {
        setInfo(null);
        setPacienteId(null);
        setPdf({ cargando: false, url: null });
        onListo(null);
    };

    const cargar = async () => {
        if (!periodo) return;
        setCargando(true);
        limpiar();
        try {
            const { data } = await axiosInstance.get("/envios/cobranza/estado", { params: { periodo } });
            const estado = data?.data;
            if (!estado.liquidada) gdAlertify.notiWarning("La cobranza de ese período no está liquidada.");
            else if (!estado.candidatos.length) gdAlertify.notiWarning("No hay pacientes en la cobranza de ese período.");
            setInfo(estado);
        } catch (error) {
            gdAlertify.notiError(mensajeError(error, "No se pudo cargar la cobranza del período."));
        } finally {
            setCargando(false);
        }
    };

    const opciones = useMemo(() => (info?.candidatos || []).map((c) => ({
        value: c.paciente_id,
        label: `${c.nombre} · ${labelTipo(c.tipo)} · $${formatMoney(c.montos.total_deuda)}`,
        c,
    })), [info]);
    const seleccion = opciones.find((o) => o.value === pacienteId) || null;
    const c = seleccion?.c || null;

    const elegirPaciente = (opcion) => {
        setPacienteId(opcion ? opcion.value : null);
        setPdf({ cargando: false, url: null });
        if (opcion) elegirTipo(opcion.c.tipo);
        else onListo(null);
    };

    const elegirTipo = (nuevo) => {
        setTipo(nuevo);
        setTemplateId(textoInicialDeTipo(info, nuevo).templateId);
    };

    // Texto del tipo: el template elegido (o el texto por defecto del tipo).
    const texto = useMemo(() => {
        if (!info || !tipo) return null;
        const template = (info.templates || []).find((t) => String(t.id) === String(templateId));
        const defecto = info.tipos?.[tipo] || {};
        return template
            ? { asunto: template.asunto || defecto.asunto || "", cuerpo: template.cuerpo || "" }
            : { asunto: defecto.asunto || "", cuerpo: defecto.cuerpo || "" };
    }, [info, tipo, templateId]);

    const faltan = useMemo(() => {
        if (!c || !texto) return [];
        return variablesEnTexto(`${texto.asunto} ${texto.cuerpo}`).filter((v) => c.variables[v] !== undefined && !String(c.variables[v]).trim());
    }, [c, texto]);
    const desconocidas = texto ? variablesDesconocidas(`${texto.asunto} ${texto.cuerpo}`) : [];

    // Cada cambio entrega al formulario de prueba el asunto y el cuerpo ya armados.
    useEffect(() => {
        if (!c || !texto) return;
        onListo({
            asunto: renderizarVariables(texto.asunto, c.variables, false),
            cuerpo: `${renderizarVariables(texto.cuerpo, c.variables)}<br/><br/>${FIRMA_DEFAULT}`,
            cobranza: { periodo, paciente_id: c.paciente_id },
            paciente: c.nombre,
            correos: c.correos,
            tipo,
            faltan,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [c, texto, tipo]);

    const verPdf = async () => {
        setPdf({ cargando: true, url: null });
        try {
            setPdf({ cargando: false, url: await imagenComoUrl("/envios/cobranza/pdf", { periodo, paciente_id: c.paciente_id }) });
        } catch (error) {
            setPdf({ cargando: false, url: null });
            gdAlertify.notiError(error.message);
        }
    };

    return (
        <div className="border rounded p-3 mb-3 bg-light">
            <div className="fw-semibold mb-2"><i className="bi bi-envelope-paper me-2"></i>Correo de cobranza de un paciente</div>
            <div className="row g-2 align-items-end mb-2">
                <div className="col-sm-8">
                    <label className="form-label small mb-1">Período (cobranza liquidada)</label>
                    <MonthInput className="form-control form-control-sm" value={periodo}
                        onChange={(e) => { setPeriodo(e.target.value); limpiar(); }} />
                </div>
                <div className="col-sm-4">
                    <button className="btn btn-sm btn-outline-primary w-100" onClick={cargar} disabled={cargando || !periodo}>
                        {cargando ? <span className="spinner-border spinner-border-sm"></span> : "Cargar"}
                    </button>
                </div>
            </div>

            {opciones.length > 0 && (
                <Select className="mb-2" placeholder="Busca el paciente…" options={opciones} value={seleccion}
                    onChange={elegirPaciente} isClearable />
            )}

            {c && (
                <>
                    <div className="row g-2 mb-2">
                        <div className="col-sm-6">
                            <label className="form-label small mb-1">Tipo de envío</label>
                            <select className="form-select form-select-sm" value={tipo} onChange={(e) => elegirTipo(e.target.value)}>
                                {TIPOS_CORREO_COBRANZA.map((t) => (
                                    <option key={t.clave} value={t.clave}>{t.label}{t.clave === c.tipo ? " (detectado)" : ""}</option>
                                ))}
                            </select>
                        </div>
                        <div className="col-sm-6">
                            <label className="form-label small mb-1">Template</label>
                            <select className="form-select form-select-sm" value={templateId} onChange={(e) => setTemplateId(e.target.value)}>
                                <option value="">Texto por defecto del tipo</option>
                                {(info.templates || []).map((t) => (
                                    <option key={t.id} value={t.id}>{t.nombre}{t.tipo ? ` (${labelTipo(t.tipo)})` : ""}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="small mb-2">
                        Notificación <strong>#{c.numero_notificacion_siguiente}</strong> · deuda ${formatMoney(c.montos.total_deuda)}
                        {c.correos.length > 0 && <> · sus correos: {c.correos.join(", ")} (la prueba va solo a los destinatarios de arriba)</>}
                    </div>
                    {c.motivo && <div className="alert alert-warning py-1 px-2 small mb-2">En el envío masivo quedaría fuera: {c.motivo}.</div>}
                    {faltan.length > 0 && (
                        <div className="alert alert-warning py-1 px-2 small mb-2">
                            Sin dato para {`{${faltan.join("}, {")}}`}: en el envío masivo este paciente no se notificaría con este texto.
                        </div>
                    )}
                    {desconocidas.length > 0 && (
                        <div className="alert alert-danger py-1 px-2 small mb-2">El texto usa variables que no existen: {`{${desconocidas.join("}, {")}}`}.</div>
                    )}

                    <button className="btn btn-sm btn-outline-secondary" onClick={verPdf} disabled={pdf.cargando}>
                        {pdf.cargando ? <span className="spinner-border spinner-border-sm me-1"></span> : <i className="bi bi-file-earmark-pdf me-1"></i>}
                        Ver PDF del detalle
                    </button>
                    {pdf.url && <iframe title="PDF del detalle" src={pdf.url} className="border rounded mt-2" style={{ width: "100%", height: 380 }} />}
                </>
            )}

            <div className="form-text mt-2">
                Se arma igual que el envío masivo (texto del tipo + variables + firma) y se adjunta el PDF del detalle.
                La prueba no cambia el número de notificación ni marca el reajuste.
            </div>
        </div>
    );
};

export default CobranzaPrueba;
